import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  buildGrid,
  describeAudit,
  fetchAccess,
  isSelfDemotion,
  removePerson,
  saveAccess,
  type Change,
  type Level,
  type ModuleChange,
  type TabChange,
} from "@/lib/access";
import { rereadAfterViewAsChange, startViewAs } from "@/lib/viewAs";
import { AddPersonBar } from "./AddPersonBar";
import { ModuleAccessRow } from "./ModuleAccessRow";

const NEXT: Record<string, Level | null> = { none: "view", view: "edit", edit: null };
const LABEL: Record<string, string> = { none: "—", view: "view", edit: "EDIT" };

/**
 * S8 · Access administration (UX v0.3 W4).
 *
 * The grid is the point: the whole permission shape at once, because a
 * spot-the-anomaly scan beats a per-person form. Every cell is stated — `—`
 * for none, never a blank the reader has to interpret.
 *
 * Three rules this screen enforces rather than assumes:
 *
 * * **Never an optimistic lie.** A failed save reverts the cell visibly and
 *   says so (UX S8 error). The grid re-reads from the server after every
 *   change rather than patching itself from what it hoped happened.
 * * **Self-demotion is confirmed by consequence**, not by a generic "are you
 *   sure" — it is the one change the person cannot reverse for themselves.
 * * **Owners see their own areas only.** That narrowing happens at the API;
 *   this screen renders whatever it is given, so there is one place the rule
 *   lives.
 */
export function AccessGrid({
  actorEmail,
  canManagePeople = false,
}: {
  actorEmail: string;
  /** Portal admin only — see `/v1/me.can_manage_people`. An area owner sets
   * levels in their own area but does not put people on the portal, so they
   * are shown no control that would refuse on click. */
  canManagePeople?: boolean;
}) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  // One person open at a time. Several expansions at once would push the grid
  // rows apart until the scan the grid exists for is impossible.
  const [openPerson, setOpenPerson] = useState<string | null>(null);
  // One view-as at a time: a second click while the first is opening is refused
  // by the API's read-only rule (403), because the first one already started it.
  // The state disables the button; the ref closes the gap before that re-render lands
  // (two clicks in one frame both read the closure's `false`).
  const [openingViewAs, setOpeningViewAs] = useState(false);
  const openingViewAsRef = useRef(false);

  const access = useQuery({
    queryKey: ["access"],
    queryFn: ({ signal }) => fetchAccess(signal),
    retry: false,
  });

  const save = useMutation({
    mutationFn: async (changes: Array<Change | ModuleChange | TabChange>) => {
      const message = await saveAccess(changes);
      if (message) throw new Error(message);
    },
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ["access"] });
      // The rail is entitlement-shaped too, so it has to re-read or the person
      // who just granted themselves an area would not see it appear.
      void queryClient.invalidateQueries({ queryKey: ["me"] });
    },
    onError: (err: Error) => setError(err.message),
  });

  if (access.isPending) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-7 w-40" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (access.isError) {
    return (
      <Alert variant="destructive" className="max-w-2xl">
        <AlertTitle>We can&rsquo;t show access right now</AlertTitle>
        <AlertDescription>{(access.error as Error).message}</AlertDescription>
      </Alert>
    );
  }

  const { areas, entitlements, people, recent } = access.data;
  // Both absent on an API older than this change — the screen then offers no
  // per-module control at all, rather than an empty one that cannot save.
  const catalogue = access.data.modules ?? [];
  const moduleEntitlements = access.data.module_entitlements ?? [];
  const tabEntitlements = access.data.tab_entitlements ?? [];
  const modulesSupported = catalogue.length > 0;
  const rows = buildGrid(areas, entitlements, people ?? []);

  const reread = () => {
    setError(null);
    void queryClient.invalidateQueries({ queryKey: ["access"] });
    void queryClient.invalidateQueries({ queryKey: ["me"] });
  };

  const remove = async (personEmail: string) => {
    // Named by consequence, like the self-demotion confirm: removal is not the
    // same act as setting every cell to `—`, and the sentence has to say which
    // one is about to happen.
    if (!window.confirm(`${personEmail} will lose the portal entirely, in every area.`)) return;
    const message = await removePerson(personEmail);
    if (message) {
      setError(message);
      return;
    }
    reread();
  };

  const viewAs = async (personEmail: string) => {
    if (openingViewAsRef.current) return;
    openingViewAsRef.current = true;
    setOpeningViewAs(true);
    try {
      const message = await startViewAs(personEmail);
      if (message) {
        setError(message);
        return;
      }
      // `/v1/me` carries the mode, so re-reading it is what puts the banner up —
      // nothing here remembers being in view-as (UX F5). Past the browser's cached
      // copy, or the banner and nav stay the owner's for up to a minute (bug 3f80402f).
      await rereadAfterViewAsChange(queryClient);
    } finally {
      openingViewAsRef.current = false;
      setOpeningViewAs(false);
    }
  };

  const cycle = (personEmail: string, areaKey: string, held: string) => {
    const change: Change = { person_email: personEmail, area_key: areaKey, level: NEXT[held] };
    if (isSelfDemotion(actorEmail, entitlements, change)) {
      const areaName = areas.find((a) => a.area_key === areaKey)?.display_name ?? areaKey;
      // Name the consequence, not the action. "Are you sure?" is a question
      // nobody has the information to answer.
      const consequence =
        change.level === null
          ? `You'll lose all access to ${areaName}.`
          : `You'll lose edit rights in ${areaName}.`;
      if (!window.confirm(consequence)) return;
    }
    save.mutate([change]);
  };

  // No self-demotion confirm here, deliberately. That guard exists for the one
  // change nobody can reverse for themselves — losing edit on an AREA, which
  // is where the access screen itself lives. Closing your own module is
  // undone from this same screen a moment later.
  const cycleModule = (change: ModuleChange) => save.mutate([change]);
  // Same reasoning for a tab: closing your own tab is undone from this screen.
  const cycleTab = (change: TabChange) => save.mutate([change]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-ks-ink">Access</h1>
        <p className="text-sm text-ks-ink">
          Who can see and act in each area. Changes apply within a minute.
          {modulesSupported && " Open Modules on a row to set one module differently from its area."}
        </p>
      </div>

      {error && (
        <Alert variant="destructive" className="max-w-2xl">
          <AlertTitle>Not saved &mdash; try again</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {canManagePeople && (
        <AddPersonBar existingEmails={rows.map((r) => r.person_email)} onAdded={reread} />
      )}

      {rows.length === 0 ? (
        <p className="text-ks-ink">
          Nobody is on the portal yet
          {canManagePeople ? " — add the first person above." : "."}
        </p>
      ) : (
        // Wide grids scroll inside their own container; the page never scrolls
        // sideways.
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Person</TableHead>
                {areas.map((area) => (
                  <TableHead key={area.area_key}>{area.display_name}</TableHead>
                ))}
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.flatMap((row) => [
                <TableRow key={row.person_email}>
                  <TableCell className="whitespace-nowrap">{row.person_email}</TableCell>
                  {row.areaKeys.map((key) => {
                    const held = row.cells[key];
                    return (
                      <TableCell key={key}>
                        <button
                          type="button"
                          disabled={save.isPending}
                          onClick={() => cycle(row.person_email, key, held)}
                          aria-label={`${row.person_email} in ${key}: ${held}. Change.`}
                        >
                          <Badge variant={held === "edit" ? "default" : "secondary"}>
                            {LABEL[held]}
                          </Badge>
                        </button>
                      </TableCell>
                    );
                  })}
                  <TableCell className="whitespace-nowrap">
                    <div className="flex gap-2">
                      {/* Seeing what someone else sees beats reading a row of
                          badges and inferring it — the row states the grants,
                          view-as states the result. */}
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={openingViewAs || row.person_email === actorEmail.toLowerCase()}
                        onClick={() => void viewAs(row.person_email)}
                      >
                        View as
                      </Button>
                      {modulesSupported && (
                        <Button
                          variant="secondary"
                          size="sm"
                          aria-expanded={openPerson === row.person_email}
                          onClick={() =>
                            setOpenPerson(
                              openPerson === row.person_email ? null : row.person_email,
                            )
                          }
                        >
                          {openPerson === row.person_email ? "Hide modules" : "Modules"}
                        </Button>
                      )}
                      {canManagePeople && (
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled={row.person_email === actorEmail.toLowerCase()}
                          onClick={() => void remove(row.person_email)}
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>,
                openPerson === row.person_email ? (
                  <ModuleAccessRow
                    key={`${row.person_email}-modules`}
                    personEmail={row.person_email}
                    areas={areas}
                    catalogue={catalogue}
                    moduleEntitlements={moduleEntitlements}
                    tabEntitlements={tabEntitlements}
                    entitlements={entitlements}
                    // Person + one per area + Actions.
                    columnCount={areas.length + 2}
                    disabled={save.isPending}
                    onChange={cycleModule}
                    onTabChange={cycleTab}
                  />
                ) : null,
              ])}
            </TableBody>
          </Table>
        </div>
      )}

      {recent.length > 0 && (
        <section>
          <h2 className="mb-1 text-sm font-bold text-ks-ink">Recent changes</h2>
          <ul className="space-y-1">
            {recent.slice(0, 10).map((entry, index) => (
              <li key={`${entry.changed_at}-${index}`} className="text-sm text-ks-grey-1">
                {describeAudit(entry, areas)}
              </li>
            ))}
          </ul>
        </section>
      )}

      <Button variant="secondary" onClick={() => void access.refetch()}>
        Refresh
      </Button>
    </div>
  );
}
